// The one line across the app that tells the account owner its subscription needs it (B1.3 — the banner of the owner's decision
// 1): a failed payment (amber, the days left of its 7, Update card), the AI paused after them (red — the phone menu still answers),
// a subscription ending at the period's end (amber, Resume). Nothing otherwise. Read on load, every 10 minutes and when the window
// comes back into focus. It must be seen (the owner's U5: the first, calm one blended into the page): a solid colour for the two
// that stop service, a strong amber edge for the ending one, and its own words when its button fails.
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { CreditCard, RotateCcw } from "lucide-react";
import axiosInstance from "../../../services/axiosInstance";

const EVERY_MS = 10 * 60 * 1000;
const RESUME_ERRORS = { already_ended: "resumeEnded", not_cancelling: "resumeNotEnding", stripe_refused: "resumeStripe", still_ending: "resumeStripe" };

export default function BillingBanner({ onOpenBilling }) {
  const { t, i18n } = useTranslation("billing");
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(null);   // the key of what the button could not do
  const load = useCallback(() => {
    axiosInstance.get("/billing/status").then((r) => setS(r.data)).catch(() => setS(null));
  }, []);
  useEffect(() => {
    load();
    const h = setInterval(load, EVERY_MS);
    window.addEventListener("focus", load);
    return () => { clearInterval(h); window.removeEventListener("focus", load); };
  }, [load]);

  const kind = s?.status === "suspended" ? "suspended"
    : (s?.status === "past_due" || s?.status === "grace") ? "pastDue"
    : (s?.status === "active" && s?.cancelAtPeriodEnd) ? "ending" : null;
  if (!kind) return null;

  const date = s.currentPeriodEnd ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(new Date(s.currentPeriodEnd)) : "—";
  const portal = async () => {
    setBusy(true); setFailed(null);
    try { const { data } = await axiosInstance.get("/billing/portal"); window.open(data.url, "_blank", "noopener"); }
    catch { onOpenBilling?.(); }   // no billing account to open: the Billing page says the rest
    finally { setBusy(false); }
  };
  const resume = async () => {
    setBusy(true); setFailed(null);
    try { await axiosInstance.post("/billing/resume"); load(); }
    catch (e) { setFailed(RESUME_ERRORS[e?.response?.data?.code] || "resumeFailed"); }
    finally { setBusy(false); }
  };
  const text = kind === "suspended" ? t("bannerSuspended") : kind === "pastDue" ? t("bannerPastDue", { days: s.daysLeft ?? 0 }) : t("bannerEnding", { date });
  const solid = kind !== "ending";
  const action = kind === "ending"
    ? (s.paidByStripe && <Button variant="contained" color="warning" size="small" onClick={resume} disabled={busy}
        startIcon={busy ? <CircularProgress size={13} color="inherit" /> : <RotateCcw size={14} />}
        sx={{ fontWeight: 700, textTransform: "none", whiteSpace: "nowrap", boxShadow: "none" }}>{t("bannerResume")}</Button>)
    : <Button variant="outlined" color="inherit" size="small" onClick={s.billingAccount ? portal : onOpenBilling} disabled={busy}
        startIcon={busy ? <CircularProgress size={13} color="inherit" /> : <CreditCard size={14} />}
        sx={{ fontWeight: 700, textTransform: "none", whiteSpace: "nowrap", borderColor: "currentColor" }}>{t("bannerUpdateCard")}</Button>;

  return (
    <Box sx={{ mb: 2.5 }} role="status" aria-live="polite">
      <Alert severity={kind === "suspended" ? "error" : "warning"} variant={solid ? "filled" : "standard"} action={action || null}
        sx={{
          borderRadius: "12px", alignItems: "center", fontSize: "0.9375rem", fontWeight: 600, py: 1,
          ...(solid ? {} : { border: "1px solid var(--warning)", borderInlineStartWidth: "6px" }),
        }}>
        {text}
        {failed && <Box component="span" sx={{ display: "block", fontWeight: 500, fontSize: "0.8125rem", mt: 0.5 }}>{t(failed)}</Box>}
      </Alert>
    </Box>
  );
}
